import { pick } from 'ramda';
import { v4 as uuidv4 } from 'uuid';
import { ReactNode } from 'react';

type PopperReference = Element | Selection | null | undefined;

export const centeredPopperElement = (reference: PopperReference) => ({
  getBoundingClientRect(): Partial<DOMRect> {
    if (!reference) {
      return {};
    }

    const searchRef = reference instanceof Selection ? (reference.anchorNode?.parentElement as Element) : reference;
    const pageWrapper = searchRef?.closest('.page-wrapper');
    if (!pageWrapper) {
      return {};
    }
    const pageBox = pageWrapper.getBoundingClientRect();

    const referenceElement = reference instanceof Selection ? reference.getRangeAt(0) : reference;
    const referenceBox = referenceElement.getBoundingClientRect();

    return {
      ...pick(['top', 'bottom', 'height', 'y'], referenceBox),
      ...pick(['left', 'right', 'width', 'x'], pageBox),
    };
  },
});

type SlateAttributes = { ref: { current: unknown } };
type RefSetter = ((ref: unknown) => void) | { current: unknown };

/**
 * Helper function to use when you need to get hold of the reference that slate uses for nodes
 * @param attributes the slate attributes
 * @param setRef your own reference, set state function or ref
 */
export const refHandler = (attributes: SlateAttributes, setRef: RefSetter) => (ref: unknown) => {
  attributes.ref.current = ref;
  if (setRef instanceof Function) {
    setRef(ref);
  } else {
    setRef.current = ref;
  }
};

/**
 * Returns a new copy of the object where all occurances of uuid are changed to newly generated uuids.
 */
export const evolveUuids = (obj: unknown): unknown => {
  if (Array.isArray(obj)) {
    return obj.map((item) => evolveUuids(item));
  }
  if (obj !== null && typeof obj === 'object') {
    const out: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(obj)) {
      out[key] = key === 'uuid' ? uuidv4() : evolveUuids(value);
    }
    return out;
  }
  return obj;
};

/**
 * Returns a single element or array of elements that have had their line breaks replaced with <br> elements.
 */
export const replaceLineBreaks = (value: string): ReactNode | ReactNode[] => {
  if (!value) return '';

  const lineSeparatorsRegex = /(?:\r\n|\r|\n|\u2028|\u2029)/g;

  const normalizedValue = value.replace(lineSeparatorsRegex, '\n');
  const valueParts = normalizedValue.split('\n');

  return (
    valueParts.map((line, index) => {
      // Skip adding line break to the last line
      if (index === valueParts.length - 1) return line;
      return (
        <>
          {line}
          <br className="excelcell" />
        </>
      );
    }) ?? ''
  );
};

type Rgba = { r: number; g: number; b: number; a?: number };

export const rgbaToHex = ({ r, g, b, a = 1 }: Rgba): string => {
  // MERGE-REVIEW: consolidated (0.2.18/0.3.0) only appends the alpha channel when a < 1,
  // so opaque colors stay 6-digit (#rrggbb). The blueprint always appended alpha (#rrggbbff).
  // Kept the consolidated behavior to preserve existing output.
  const includeAlpha = a != null && a < 1;
  const channels = [r, g, b, ...(includeAlpha ? [Math.round(a * 255)] : [])];
  return '#' + channels.map((v) => Math.round(v).toString(16).padStart(2, '0')).join('');
};
